'use client';

import { useState } from 'react';
import { dataLayerPush } from '@libs/analytics';
import { activateOffer, getOffers as fetchMemberOffers } from '@libs/api-proxy-client';
import { AccountDrawerType, logSentryError, SentryAcpTreatsRewardsErrors as SentryErrors, useAlertBanner, useLoyaltyContext, useOptimizely, usePageLayout } from '@libs/common-next';
import type { Offer } from '@libs/common-types';
import { ensureAuth0AccessToken, evaluateSession, isUserLoggedIn } from '@libs/core';
import { SnackbarUtils } from '@libs/shared-ui/utils';
import { applyMemberState } from '../../utils/apply-member-state';
import { type OfferPageSection, useOfferDrawerStore } from './useOfferDrawerStore';
export type ActivateRewardTracking = {
  pageSection: string;
  selection: string;
};
type PostLoginAction = 'openDetails' | 'activate';
type ActivateOfferOptions = {
  activateReward?: ActivateRewardTracking;
  postLoginAction?: PostLoginAction;
};
export function getCardSize(offer?: Offer, isFeatured?: boolean) {
  if (isFeatured) {
    return 'featured';
  }
  return offer?.cardSize.toLowerCase() === 'large' ? 'large' : 'small';
}
export function useOfferCardActions() {
  const {
    getOffers,
    isActivating
  } = useLoyaltyContext();
  const {
    drawerOpen,
    selectedOffer,
    offerPageSection,
    openOffer,
    closeOffer,
    markOfferActivated
  } = useOfferDrawerStore();
  const {
    isAuthenticated,
    openAccountDrawer,
    setSignInDrawerProps,
    user
  } = usePageLayout();
  const {
    setAlert
  } = useAlertBanner();
  const {
    context
  } = useOptimizely();
  const [activatingOffers, setActivatingOffers] = useState<Set<string>>(new Set());
  function handleCardClick(offer: Offer, pageSection?: OfferPageSection) {
    openOffer(offer, pageSection);
    context?.trackEvent('offer_details_modal_click');
    // Reset error so pre-existing error doesn't display inside offer drawer
    setAlert(undefined);
  }
  function handleCloseDrawer() {
    closeOffer();

    // Reset error so error from offer drawer doesn't display on offer grid
    setAlert(undefined);
  }
  async function handleActivateOffer(offer_0?: Offer, gtmSection?: OfferPageSection, {
    activateReward,
    postLoginAction = 'openDetails'
  }: ActivateOfferOptions = {}) {
    if (!offer_0?.isActivatable || offer_0.isActivated) {
      return;
    }
    if (!isAuthenticated && !user) {
      setSignInDrawerProps({
        onSuccess: () => handleOfferSignInSuccess(offer_0, postLoginAction, gtmSection, activateReward)
      });
      openAccountDrawer(AccountDrawerType.SIGN_IN);
      return;
    }
    await activateAuthenticatedOffer(offer_0, gtmSection, activateReward);
  }
  async function handleOfferSignInSuccess(offer_1: Offer, postLoginAction_0: PostLoginAction, gtmSection_0?: OfferPageSection, activateReward_0?: ActivateRewardTracking) {
    try {
      const response = await fetchMemberOffers();
      if (!response) {
        throw new Error('Failed to fetch offers after sign-in');
      }
      if (!isUserLoggedIn()) return;
      const memberOffer = response.offers.find(candidate => candidate.offerId === offer_1.offerId);
      if (!memberOffer) return;
      const offerWithMemberState = applyMemberState(offer_1, memberOffer);
      openOffer(offerWithMemberState, gtmSection_0);
      if (postLoginAction_0 === 'activate') {
        await activateAuthenticatedOffer(offerWithMemberState, gtmSection_0, activateReward_0);
      }
    } catch (error) {
      SnackbarUtils.showPredefined('SERVER_ERROR');
      logSentryError(error, SentryErrors.OfferCard.ActivateOfferFailed);
    }
  }
  async function activateAuthenticatedOffer(offer_2: Offer, gtmSection_1?: OfferPageSection, activateReward_1?: ActivateRewardTracking) {
    if (!offer_2.isActivatable || offer_2.isActivated) return;
    setActivatingOffers(prevOffers => new Set(prevOffers).add(offer_2.offerId));
    try {
      const sessionStillValid = await evaluateSession({});
      if (!sessionStillValid) {
        return;
      }
      await ensureAuth0AccessToken();
      if (!isUserLoggedIn()) {
        return;
      }
      const response_0 = await activateOffer(offer_2);
      if (response_0 && 'success' in response_0 && response_0?.success) {
        setAlert(undefined);
        context?.trackEvent('offer_activations');
        if (activateReward_1) {
          dataLayerPush({
            event: 'activate_reward',
            page_section: activateReward_1.pageSection,
            selection: activateReward_1.selection
          });
        }

        // Update selectedOffer immediately to reflect activated state
        markOfferActivated(offer_2.offerId);
        // Fetch offers again from API to get updated data after activation
        await getOffers();
      } else {
        throw new Error(`Failed to activate an offer, offerId: ${offer_2.offerId}`);
      }
    } catch (error_0) {
      SnackbarUtils.showPredefined('SERVER_ERROR');
      dataLayerPush({
        event: 'error',
        site_section: gtmSection_1 || 'treats rewards',
        error_message: `uh oh! [${offer_2.shortDescription}] order didn't activate. please try again.`
      });
      logSentryError(error_0, SentryErrors.OfferCard.ActivateOfferFailed);
    } finally {
      setActivatingOffers(prevOffers => {
        const updatedSet = new Set(prevOffers);
        updatedSet.delete(offer_2.offerId);
        return updatedSet;
      });
    }
  }
  function showSpinner(offer_3?: Offer) {
    return offer_3 ? activatingOffers.has(offer_3.offerId) : false;
  }
  return {
    drawerOpen,
    handleActivateOffer,
    handleCardClick,
    handleCloseDrawer,
    isActivating,
    offerPageSection,
    selectedOffer,
    showSpinner
  };
}